import styled from '@emotion/styled'
import {
  AuthorisedUser,
  CrudActions,
  DealActions,
  DealDetail,
  DealServicingRespondAttributes,
  DealServicingRespondBulkArgs,
  DealServicingStartAttributes,
  DealServicingStartBulkArgs,
  EntityType,
  MidTradeAction,
  SpreadsheetMapping,
  SpreadsheetMappingType
} from 'api'
import { Locale, Translation } from 'locale'
import { isNil, noop } from 'lodash-es'
import React, { useEffect, useState } from 'react'
import { connect } from 'react-redux'
import { createStructuredSelector } from 'reselect'
import { createAuthorisedUserSelector } from 'selectors/connectionSelectors'
import { dealDetailGetterSelector } from 'selectors/dealSelectors'
import { createEntityGetterSelector } from 'selectors/entitySelectors'
import { createSortedSpreadsheetMappingIdsSelector } from 'selectors/spreadsheetMappingSelectors'
import { excelService, ioService } from 'services'
import { NotificationActions, ShowGrowlArgs } from 'store/notification'
import {
  dealPreloadHasCompleted,
  dealPreloadHasError,
  dealPreloadIsLoading,
  DealPreloadState,
  dealPreloadStateSelector,
  SpreadsheetUploadActions
} from 'store/spreadsheetUpload'
import { ButtonStrip, TextButton, TextButtonType } from 'view/components/buttons'
import { Groupbox, ModalContent, ModalFooter, ModalWindow } from 'view/components/panels'
import * as constants from 'view/styles/constants'
import { transformHaircutInput, transformRateInput } from '../datagrid'
import { SpreadsheetMappingSelectboxField } from '../entity'
import { ModalConsumerProps, NotificationType, withModal } from '../modal'
import { ParseResultPanel, UploadDropzoneEmpty, UploadDropzoneFull } from '../upload'
import { cloneMapping, getDefaultMapping, parseClipboard, parseString } from '../upload/model'
import { MidTradeEventMappingDialog } from './MidTradeEventMappingDialog'
import {
  MidTradeEventAction,
  MidTradeEventSpreadsheetParseResult,
  parseMidTradeEventSpreadsheet,
  ValidMidTradeEventAction
} from './parseMidTradeEventSpreadsheet'
import { PendingMidTradeEventPanel } from './PendingMidTradeEventPanel'
import { PreloadingTradesMessage } from './PreloadingTradesMessage'
import { validateMidTradeEventParseResult } from './validateMidTradeEventParseResult'

export type MidTradeEventUploadDialogProps = OwnProps & StateProps & DispatchProps

interface OwnProps extends ModalConsumerProps {
  incoming?: boolean
  initialFile?: File
  initialClipboard?: string
  onClose?: () => unknown
}

interface StateProps {
  dealPreloadState: DealPreloadState
  authorisedUser: AuthorisedUser
  spreadsheetMappings: string[]
  getSpreadsheetMapping: (mappingId: string) => SpreadsheetMapping | null
  getDealDetail: (dealId: string) => DealDetail | null
}

interface DispatchProps {
  onDeleteSpreadsheetMapping: (mappingId: string) => unknown
  onShowGrowl: (growlArgs: ShowGrowlArgs) => unknown
  onServicingStartBulk: (args: DealServicingStartBulkArgs) => unknown
  onServicingRespondBulk: (args: DealServicingRespondBulkArgs) => unknown
  onDealsUploaded: (tradeIds: string[]) => void
  onDealsUploadInitialise: () => void
}

interface MidTradeEventUploadDialogState {
  uploadFile?: File
  clipboard?: string
  rowData: any[] | null
  parseResult: MidTradeEventSpreadsheetParseResult | null
  spreadsheetMappingId: string | null
  validMidTradeEventActions: ValidMidTradeEventAction[]
  invalidMidTradeEventActions: MidTradeEventAction[]
}

const mapStateToProps = createStructuredSelector({
  authorisedUser: createAuthorisedUserSelector(),
  spreadsheetMappings: createSortedSpreadsheetMappingIdsSelector(() => SpreadsheetMappingType.MID_TRADE),
  getSpreadsheetMapping: createEntityGetterSelector(EntityType.SPREADSHEET_MAPPING),
  getDealDetail: dealDetailGetterSelector,
  dealPreloadState: dealPreloadStateSelector
})

const mapDispatchToProps = {
  onDeleteSpreadsheetMapping: CrudActions.spreadsheetMappingDelete,
  onShowGrowl: NotificationActions.showGrowl,
  onServicingStartBulk: DealActions.servicingStartBulk,
  onServicingRespondBulk: DealActions.servicingRespondBulk,
  onDealsUploaded: SpreadsheetUploadActions.mteDealsUploaded,
  onDealsUploadInitialise: SpreadsheetUploadActions.mteUploadUnsubscribe
}

export const Content = styled(ModalContent)`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  padding: 0;
  margin: 0 24px;
  width: 1100px;
  min-height: 500px;
  border-bottom: 1px solid ${constants.CORE_PRIMARY_1};
`

const UploadContainer = styled.section`
  flex-grow: 1;
  max-width: 450px;
`

const PendingContainer = styled.section`
  width: 620px;
`

const MidTradeEventUploadDialogComponent: React.FunctionComponent<MidTradeEventUploadDialogProps> = props => {
  const {
    incoming,
    initialFile,
    initialClipboard,
    spreadsheetMappings,
    authorisedUser,
    dealPreloadState,
    getSpreadsheetMapping,
    getDealDetail,
    onDeleteSpreadsheetMapping,
    onServicingStartBulk,
    onServicingRespondBulk,
    onClose: _onClose = noop,
    onShowGrowl,
    onDealsUploaded,
    onDealsUploadInitialise,
    showDialog,
    showModal
  } = props

  const initialState = {
    uploadFile: initialFile,
    clipboard: initialClipboard,
    rowData: null,
    parseResult: null,
    spreadsheetMappingId: null,
    validMidTradeEventActions: [],
    invalidMidTradeEventActions: []
  }
  const [componentState, setState] = useState<MidTradeEventUploadDialogState>(initialState)

  const {
    uploadFile,
    clipboard,
    spreadsheetMappingId,
    rowData,
    parseResult,
    validMidTradeEventActions,
    invalidMidTradeEventActions
  } = componentState

  const onClose = () => {
    onDealsUploadInitialise()
    _onClose()
  }

  const loadRowData = async () => {
    try {
      setState(state => ({ ...state, rowData: null, parseResult: null }))

      if (uploadFile) {
        const newFileData = await ioService.readFile(uploadFile)
        const newRowData = await excelService.parseXlsx(newFileData, { fileType: uploadFile.type })
        setState(state => ({ ...state, rowData: newRowData }))
      } else if (clipboard) {
        const newRowData = parseClipboard(clipboard)
        setState(state => ({ ...state, rowData: newRowData }))
      }
    } catch (err) {
      onShowGrowl({
        type: NotificationType.Error,
        title: Translation.TC_UPLOAD_ERROR_TITLE,
        message: Translation.SC_UPLOAD_ERROR_MESSAGE,
        messageArgs: err
      })
    }
  }

  useEffect(() => {
    if (spreadsheetMappings.length === 1) {
      setState(state => ({ ...state, spreadsheetMappingId: spreadsheetMappings[0] }))
    }
    loadRowData()
  }, [])

  useEffect(() => {
    loadRowData()
  }, [uploadFile, clipboard])

  useEffect(() => {
    const spreadsheetMapping = getSelectedMapping()
    if (rowData && spreadsheetMapping) {
      const tradeIds = rowData.reduce((ids, row, idx) => {
        const result = parseString(row, idx + 1, spreadsheetMapping.tradeId, Locale.translate(Translation.TC_DEAL_ID), true)
        return result.isValue ? [...ids, result.value] : ids
      }, [])

      onDealsUploaded(tradeIds)
    }
  }, [rowData, spreadsheetMappingId])

  const parseRowData = () => {
    const spreadsheetMapping = getSelectedMapping()

    if ((uploadFile || clipboard) && rowData && spreadsheetMapping) {
      const newParseResult = parseMidTradeEventSpreadsheet(
        uploadFile?.name ?? 'Clipboard',
        rowData,
        spreadsheetMapping,
        authorisedUser.userPreference,
        !!incoming,
        getDealDetail
      )
      const { validMidTradeActions, invalidMidTradeActions } = validateMidTradeEventParseResult(
        newParseResult,
        !!incoming,
        authorisedUser.userPreference,
        getDealDetail
      )

      setState(state => ({
        ...state,
        parseResult: newParseResult,
        validMidTradeEventActions: validMidTradeActions,
        invalidMidTradeEventActions: invalidMidTradeActions
      }))
    } else {
      setState(state => ({ ...state, parseResult: null, validMidTradeEventActions: [], invalidMidTradeEventActions: [] }))
    }
  }

  useEffect(() => {
    if (dealPreloadHasCompleted(dealPreloadState)) {
      parseRowData()
    }
  }, [spreadsheetMappingId, rowData, getSpreadsheetMapping, dealPreloadState])

  useEffect(() => {
    if (dealPreloadHasError(dealPreloadState)) {
      onShowGrowl({
        type: NotificationType.Error,
        title: Translation.TC_UPLOAD_ERROR_TITLE,
        message: Translation.SC_UPLOAD_PRELOAD_FAILED
      })
      onClose()
    }
  }, [dealPreloadState])

  const getSelectedMapping = () =>
    spreadsheetMappingId ? getSpreadsheetMapping(spreadsheetMappingId) : getDefaultMapping(SpreadsheetMappingType.MID_TRADE)

  const ensureSelectedMapping = () => {
    const result = getSelectedMapping()

    if (isNil(result)) {
      throw new Error('Unable to load spreadsheet configuration')
    }

    return result
  }

  const canUpload = !!parseResult && parseResult.issues.length === 0 && validMidTradeEventActions.length > 0

  const handleUpdateMapping = (mappingId: string) => {
    setState({ ...componentState, spreadsheetMappingId: mappingId })
  }

  const handleDropFiles = (files: File[]) => {
    setState({ ...componentState, uploadFile: files[0], clipboard: undefined })
  }

  const handlePaste = (clipboardContent: string) => {
    setState({ ...componentState, uploadFile: undefined, clipboard: clipboardContent })
  }

  const handleNewMapping = () => {
    showMappingDialog(cloneMapping(ensureSelectedMapping(), authorisedUser.id), Locale.translate(Translation.TC_CREATE_MAPPING))
  }

  const handleEditMapping = () => {
    showMappingDialog(ensureSelectedMapping(), Locale.translate(Translation.TC_UPDATE_MAPPING))
  }

  const handleDeleteMapping = async () => {
    const mapping = ensureSelectedMapping()

    if (mapping.id === null) {
      throw new Error('No spreadsheet mapping is selected')
    }

    const result = await showDialog({
      type: NotificationType.Confirm,
      title: Translation.TC_PROMPT_TITLE_DELETE_SPREADSHEET_MAPPING,
      message: Translation.SC_PROMPT_MSG_DELETE_SPREADSHEET_MAPPING,
      messageArgs: { name: mapping.name },
      buttons: [Translation.TC_BUTTON_YES, Translation.TC_BUTTON_NO],
      defaultButton: Translation.TC_BUTTON_YES,
      cancelButton: Translation.TC_BUTTON_NO
    })

    if (result === Translation.TC_BUTTON_YES) {
      onDeleteSpreadsheetMapping(mapping.id)
      setState({ ...componentState, spreadsheetMappingId: null })
    }
  }

  const showMappingDialog = async (mapping: SpreadsheetMapping, title: string) => {
    if (!parseResult) {
      throw new Error('Spreadsheet has not been loaded')
    }

    const mappingId = await showModal(
      <MidTradeEventMappingDialog
        mapping={mapping}
        title={title}
        parseResult={parseResult}
        userPreference={authorisedUser.userPreference}
        incoming={incoming}
        getDealDetail={getDealDetail}
      />
    )

    if (mappingId) {
      setState({ ...componentState, spreadsheetMappingId: mappingId })
    }
  }

  const handleUploadEvents = async () => {
    if (!canUpload) {
      throw new Error('No mid trade events are ready to be uploaded')
    }
    if (incoming) {
      const dealServicingRespondAttributesList: DealServicingRespondAttributes[] = validMidTradeEventActions.map(
        ({ tradeId, effectiveDate, responseType, notional, rate, haircut, haircutType, fee }) => {
          if (!responseType) {
            throw new Error(`Uploading MTE response - null mid trade event response`)
          }

          const dealDetail = getDealDetail(tradeId)
          return {
            dealId: tradeId,
            response: responseType,
            effectiveDate,
            haircut: !isNil(haircut)
              ? transformHaircutInput(
                  haircut,
                  authorisedUser.userPreference.haircutSign,
                  !!dealDetail?.ownOrder,
                  dealDetail?.direction ?? null
                )
              : null,
            haircutType,
            rate: !isNil(rate) ? transformRateInput(rate, dealDetail?.referenceRate ?? null) : null,
            notional,
            fee
          }
        }
      )

      if (dealServicingRespondAttributesList.length !== 0) {
        onServicingRespondBulk({ dealServicingRespondAttributesList })
      }
    } else {
      let recalculateCollateralPrice = false
      const hasOpenCloseAction = validMidTradeEventActions.some(midTradeEvent => midTradeEvent.actionType === MidTradeAction.RESIZE)
      if (hasOpenCloseAction) {
        const result = await showDialog({
          type: NotificationType.Confirm,
          title: Translation.TC_NEW_OR_OLD_COLLATERAL_PRICE,
          message: Translation.TC_EMPTY_HEADER,
          buttons: [Translation.TC_BUTTON_YES, Translation.TC_BUTTON_NO, Translation.TC_BUTTON_CANCEL],
          defaultButton: Translation.TC_BUTTON_YES
        })
        if (result === Translation.TC_BUTTON_CANCEL) {
          return
        } else {
          recalculateCollateralPrice = result !== Translation.TC_BUTTON_YES
        }
      }
      const dealServicingStartAttributesList: DealServicingStartAttributes[] = validMidTradeEventActions.map(
        ({ tradeId, effectiveDate, actionType, notional, rate, haircut, haircutType, fee }) => {
          if (!actionType) {
            throw new Error(`Uploading MTE - null mid trade event action`)
          }

          const dealDetail = getDealDetail(tradeId)
          const actionTypeValue =
            actionType === MidTradeAction.RESIZE
              ? recalculateCollateralPrice
                ? MidTradeAction.RESIZE_WITH_RECALCULATED_COLLATERAL_PRICE
                : MidTradeAction.RESIZE
              : actionType

          return {
            dealId: tradeId,
            action: actionTypeValue,
            effectiveDate,
            haircut: !isNil(haircut)
              ? transformHaircutInput(
                  haircut,
                  authorisedUser.userPreference.haircutSign,
                  !!dealDetail?.ownOrder,
                  dealDetail?.direction ?? null
                )
              : null,
            haircutType,
            rate: !isNil(rate) ? transformRateInput(rate, dealDetail?.referenceRate ?? null) : null,
            notional,
            fee
          }
        }
      )

      if (dealServicingStartAttributesList.length !== 0) {
        onServicingStartBulk({ dealServicingStartAttributesList })
      }
    }
    onClose()
  }

  return (
    <ModalWindow title={Locale.translate(Translation.TC_UPLOAD_MTE)}>
      <Content>
        <>
          <UploadContainer>
            <Groupbox title={Locale.translate(Translation.TC_UPLOAD_FILE)}>
              {uploadFile || clipboard ? (
                <UploadDropzoneFull
                  uploadFile={uploadFile}
                  clipboard={clipboard}
                  handleDropFiles={handleDropFiles}
                  handlePaste={handlePaste}
                />
              ) : (
                <UploadDropzoneEmpty
                  narrative={Locale.translate(Translation.SC_MTE_DROPZONE_NARRATIVE)}
                  dataTestId={'midTradeUpload-DropZone'}
                  buttonDataTestId={'UploadMidTradeEvents-ButtonBrowse'}
                  handleDropFiles={handleDropFiles}
                  handlePaste={handlePaste}
                />
              )}
            </Groupbox>
            {uploadFile || clipboard ? (
              <Groupbox title={Locale.translate(Translation.TC_COLUMN_MAPPINGS)}>
                <ParseResultPanel
                  parseResult={parseResult}
                  itemField="midTradeEventActions"
                  successMessage={Translation.SC_MTE_SPREADSHEET_SUCCESS}
                  errorMessage={Translation.SC_MTE_SPREADSHEET_ERROR}
                  errorNarrative={Translation.SC_UPLOAD_ERROR_NARRATIVE}
                />
                <SpreadsheetMappingSelectboxField
                  label={Locale.translate(Translation.SC_SAVED_MAPPINGS)}
                  placeholder={Locale.translate(Translation.SC_SYSTEM_MAPPING)}
                  value={spreadsheetMappingId}
                  canClear={true}
                  onUpdate={handleUpdateMapping}
                  mappingType={SpreadsheetMappingType.MID_TRADE}
                  disabled={!parseResult}
                />
                <ButtonStrip horizontalLayout={constants.HorizontalLayout.Right} spacing="3px">
                  <TextButton
                    dataTestId={'MidTradeEventUploadDialog-TextButton-DeleteButton'}
                    type={TextButtonType.Bad}
                    disabled={isNil(spreadsheetMappingId)}
                    onClick={handleDeleteMapping}>
                    {Locale.translate(Translation.TC_BUTTON_DELETE)}
                  </TextButton>
                  <TextButton
                    dataTestId={'MidTradeEventUploadDialog-TextButton-UpdateButton'}
                    type={TextButtonType.Secondary}
                    disabled={isNil(spreadsheetMappingId)}
                    onClick={handleEditMapping}>
                    {Locale.translate(Translation.TC_BUTTON_UPDATE)}
                  </TextButton>
                  <TextButton
                    dataTestId={'MidTradeEventUploadDialog-TextButton-CreateButton'}
                    type={TextButtonType.Secondary}
                    onClick={handleNewMapping}
                    disabled={!parseResult}>
                    {Locale.translate(Translation.TC_BUTTON_CREATE)}
                  </TextButton>
                </ButtonStrip>
              </Groupbox>
            ) : null}
          </UploadContainer>
          {dealPreloadIsLoading(dealPreloadState) ? <PreloadingTradesMessage /> : null}
          {parseResult &&
          parseResult.issues.length === 0 &&
          (validMidTradeEventActions.length > 0 || invalidMidTradeEventActions.length > 0) ? (
            <PendingContainer>
              <PendingMidTradeEventPanel
                validMidTradeEventActions={validMidTradeEventActions}
                invalidMidTradeEventActions={invalidMidTradeEventActions}
                incoming={incoming}
                handleUpload={handleUploadEvents}
                canUpload={canUpload}
              />
            </PendingContainer>
          ) : null}
        </>
      </Content>
      <ModalFooter>
        <TextButton dataTestId={'AddAssets-UploadAssets-ButtonCancel'} type={TextButtonType.Tertiary} onClick={onClose}>
          {Locale.translate(Translation.TC_BUTTON_CANCEL)}
        </TextButton>
      </ModalFooter>
    </ModalWindow>
  )
}

export const MidTradeEventUploadDialog = withModal(
  connect<StateProps, DispatchProps, OwnProps>(mapStateToProps, mapDispatchToProps)(MidTradeEventUploadDialogComponent)
)
